import React, { useState, useRef, useEffect } from 'react';
import { message, Spin, Modal, Input, Dropdown } from 'antd';
import { HistoryOutlined, SettingOutlined, DownloadOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import CanvasEditor from '../components/CanvasEditor.jsx';

// ===== 常量 =====
const RATIOS = [
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '16:9', label: '16:9' },
];
const RATIO_SIZE = { '1:1': '1024x1024', '3:4': '768x1024', '4:3': '1024x768', '16:9': '1024x576' };
// 2K 档：对应比例放大（1K 的 2 倍边长），16:9→2048x1152 等
const RATIO_SIZE_2K = { '1:1': '2048x2048', '3:4': '1536x2048', '4:3': '2048x1536', '16:9': '2048x1152' };
const COUNTS = [1, 2, 4];
const STYLES = [
  { value: 'white', label: '简约白底' },
  { value: 'scene', label: '场景' },
  { value: 'promo', label: '促销' },
];
// 任务类型 → 展示名（与后端 taskType 对应）
const TASK_LABELS = {
  PRODUCT_COMPOSITE: '跨图融合（产品+参考背景·像素直贴）',
  SCENE_COMPOSITE: '保产品换背景（AI 画新背景+产品像素直贴）',
  REMOVE_PRODUCT: '去产品保背景（就地编辑）',
  TEXT_REMOVE: '去文字/水印（就地擦除）',
  TEXT_REPLACE: '改文字（去字+真实排版）',
  FINE_EDIT: '局部编辑（就地修改）',
  SPATIAL_EDIT: '空间位移（只动物体·其余像素锁死）',
  RETOUCH_SMOOTH: '高保真去皱精修（字形像素级锁定）',
  MAIN_IMAGE_SMART: '智能主图（四维诊断→信息规划→引擎排版）',
};

// 创作指令词模板（点「创作指令词」按钮弹出选择，选中即填入输入框）
const PROMPT_TEMPLATES = [
  {
    key: 'fusion',
    label: '融合指令模板（产品图 + 参考图）',
    desc: '上传产品图 + 添加参考图后使用 · 系统自动套用防变形/真实阴影全套约束',
    prompt: '把产品图中实际可见、独立成件的物品，按原整体形态搬运到参考图场景中，只放这些物品、不加任何额外装饰物；透明袋/盒装产品作为一个整体，袋内透出的轮廓不要拆出来或额外画出；产品形状、颜色、材质、标签文字保持不变。',
  },
  {
    key: 'change_bg',
    label: '换背景模板',
    desc: '就地替换背景 · 产品保持 100% 不变',
    prompt: '把图中产品的背景替换为【新背景描述】，产品形状、颜色、材质、构图、文字、logo 完全保持不变，仅替换背景并自然融合光影。',
  },
  {
    key: 'empty_bg',
    label: '空背景模板',
    desc: '生成干净空场景背景图 · 无产品',
    prompt: '商业电商产品摄影背景场景图，干净极简，无任何产品、无文字、无水印、无 logo，中心区域留白充足，光影自然，高级质感。',
  },
  {
    key: 'remove_text',
    label: '去文字/水印模板',
    desc: '擦除画面中的文字、水印、logo',
    prompt: '去除画面中的所有文字、水印、logo，其余内容保持原样，被擦除区域自然填充背景，不留痕迹。',
  },
  {
    key: 'selling',
    label: '卖点主图模板',
    desc: '结合卖点生成主图 · 文生图',
    prompt: '为产品生成电商主图：突出核心卖点，画面干净高级，真实质感，光影自然，无多余杂物、无乱码文字。',
  },
];

// 用户收藏的常用提示词（localStorage 持久化，个人偏好）
const SAVED_PROMPTS_KEY = 'yuntu_saved_prompts_v1';

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ===== 样式（糖果珊瑚色系，复刻「生图工作台」设计稿）=====
const css = `
  .yuntu-wb { --brand:#D85A30; --brand-hover:#C14F28; --brand-soft:#FBE9E2; --brand-line:#F3C7B6;
    --blue:#EAF3F8; --bg:#FFF8F4; --card:#fff; --border:#F0E2DB; --text:#33251F; --text-2:#8A7A72; --text-3:#B9A79E;
    min-height:calc(100vh - 78px); padding-top:14px; color:var(--text); font-size:14px; line-height:1.6;
    background:radial-gradient(1200px 500px at 85% -10%, #FBE9E2 0%, transparent 55%), radial-gradient(900px 400px at -5% 110%, #EAF3F8 0%, transparent 50%), var(--bg); }
  .yuntu-wb * { box-sizing:border-box; }
  .yuntu-wb .topbar { display:flex; align-items:center; gap:14px; height:58px; padding:0 22px;
    background:rgba(255,255,255,.78); backdrop-filter:blur(12px); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:10; }
  .yuntu-wb .logo { display:flex; align-items:center; gap:9px; font-size:16px; font-weight:600; }
  .yuntu-wb .logo .cloud { width:28px; height:28px; border-radius:8px; background:linear-gradient(135deg,#E0693F,#D85A30 60%,#C14F28);
    display:inline-flex; align-items:center; justify-content:center; color:#fff; font-size:15px; box-shadow:0 3px 8px rgba(216,90,48,.28); }
  .yuntu-wb .logo .sub { font-weight:400; font-size:12px; color:var(--text-3); margin-left:2px; }
  .yuntu-wb .spacer { flex:1; }
  .yuntu-wb .model-select { display:inline-flex; align-items:center; gap:7px; padding:7px 13px; border:1px solid var(--border);
    border-radius:9px; background:#fff; cursor:pointer; transition:.15s; }
  .yuntu-wb .model-select:hover { border-color:var(--brand-line); }
  .yuntu-wb .model-select b { font-weight:600; font-size:13px; }
  .yuntu-wb .model-select .tag { font-size:11px; color:var(--brand); background:var(--brand-soft); padding:1px 7px; border-radius:5px; font-weight:500; }
  .yuntu-wb .icon-btn { width:34px; height:34px; border-radius:9px; border:1px solid var(--border); background:#fff; cursor:pointer; color:var(--text-2);
    font-size:15px; display:inline-flex; align-items:center; justify-content:center; transition:.15s; }
  .yuntu-wb .icon-btn:hover { border-color:var(--brand-line); color:var(--brand); background:var(--brand-soft); }
  .yuntu-wb .layout { display:flex; gap:20px; padding:18px 22px 18px 20px; align-items:flex-start; width:100%; }
  .yuntu-wb .sidebar { width:250px; flex-shrink:0; display:flex; flex-direction:column; gap:14px; }
  .yuntu-wb .main { flex:1; display:flex; flex-direction:column; gap:16px; min-width:0; }
  .yuntu-wb .card { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:16px; box-shadow:0 1px 2px rgba(216,90,48,.05),0 4px 16px rgba(216,90,48,.05); }
  .yuntu-wb .card h3 { font-size:13px; font-weight:600; color:var(--text-2); margin-bottom:12px; display:flex; align-items:center; justify-content:space-between; }
  .yuntu-wb .card h3 .link { font-size:12px; color:var(--brand); cursor:pointer; font-weight:400; }
  .yuntu-wb .upload { border:1.5px dashed #E4CDC2; border-radius:10px; padding:22px 12px; text-align:center; color:var(--text-3); cursor:pointer; transition:.15s; }
  .yuntu-wb .upload:hover { border-color:var(--brand); color:var(--brand); background:#FFFCFA; }
  .yuntu-wb .upload .plus { font-size:22px; line-height:1; }
  .yuntu-wb .upload .sub { font-size:12px; margin-top:5px; }
  .yuntu-wb .product-thumb { position:relative; border-radius:10px; overflow:hidden; border:1px solid var(--border); background:#FBF6F3; aspect-ratio:1/1; display:flex; align-items:center; justify-content:center; }
  .yuntu-wb .product-thumb img { width:100%; height:100%; object-fit:contain; }
  .yuntu-wb .product-thumb .reup { position:absolute; inset:0; background:rgba(51,37,31,.45); color:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; opacity:0; transition:.18s; cursor:pointer; font-size:12px; }
  .yuntu-wb .product-thumb:hover .reup { opacity:1; }
  .yuntu-wb .tags { display:flex; flex-wrap:wrap; gap:6px; }
  .yuntu-wb .tag-chip { font-size:12px; padding:4px 11px; border-radius:20px; background:var(--brand-soft); color:var(--brand); border:1px solid var(--brand-line); cursor:pointer; transition:.15s; }
  .yuntu-wb .tag-chip:hover { background:#F7DCCF; }
  .yuntu-wb .tag-chip.off { background:#fff; color:var(--text-3); border-color:var(--border); }
  .yuntu-wb .refs { display:flex; gap:8px; flex-wrap:wrap; }
  .yuntu-wb .ref { width:56px; height:56px; border-radius:9px; border:1px solid var(--border); background:linear-gradient(135deg,#FBF1EC,#EAF3F8); position:relative; overflow:hidden; cursor:pointer; transition:.15s; }
  .yuntu-wb .ref img { width:100%; height:100%; object-fit:cover; position:relative; z-index:1; }
  .yuntu-wb .ref .del { position:absolute; top:0; right:0; width:16px; height:16px; background:rgba(0,0,0,.5); color:#fff; font-size:11px; line-height:16px; text-align:center; z-index:2; border-radius:0 0 0 6px; }
  .yuntu-wb .ref.add { border-style:dashed; border-color:#E4CDC2; display:flex; align-items:center; justify-content:center; color:var(--text-3); font-size:18px; }
  .yuntu-wb .product-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
  .yuntu-wb .pcell { position:relative; border-radius:9px; overflow:hidden; border:2px solid var(--border); background:#FBF6F3; aspect-ratio:1/1; cursor:pointer; transition:.15s; }
  .yuntu-wb .pcell:hover { border-color:var(--brand-line); }
  .yuntu-wb .pcell.active { border-color:var(--brand); box-shadow:0 0 0 2px var(--brand-soft); }
  .yuntu-wb .pcell img { width:100%; height:100%; object-fit:contain; }
  .yuntu-wb .pcell .pdel { position:absolute; top:4px; right:4px; width:18px; height:18px; border-radius:50%; background:rgba(0,0,0,.55); color:#fff; font-size:13px; line-height:18px; text-align:center; z-index:3; }
  .yuntu-wb .pcell .prep { position:absolute; bottom:4px; right:4px; width:20px; height:20px; border-radius:50%; background:rgba(0,0,0,.55); color:#fff; font-size:13px; line-height:20px; text-align:center; z-index:3; }
  .yuntu-wb .pcell .pbadge { position:absolute; top:4px; left:4px; font-size:10px; padding:1px 6px; border-radius:8px; background:var(--brand); color:#fff; z-index:3; }
  .yuntu-wb .pcell.add { border-style:dashed; border-color:#E4CDC2; display:flex; align-items:center; justify-content:center; color:var(--text-3); font-size:22px; background:#fff; }
  .yuntu-wb .param-row { margin-bottom:13px; }
  .yuntu-wb .param-row:last-child { margin-bottom:0; }
  .yuntu-wb .param-row .label { font-size:12px; color:var(--text-2); margin-bottom:7px; display:block; }
  .yuntu-wb .seg { display:flex; gap:6px; }
  .yuntu-wb .seg .opt { flex:1; text-align:center; padding:6px 0; font-size:12px; border:1px solid var(--border); border-radius:7px; cursor:pointer; color:var(--text-2); transition:.14s; white-space:nowrap; }
  .yuntu-wb .seg .opt:hover { border-color:var(--brand-line); color:var(--brand); }
  .yuntu-wb .seg .opt.active { border-color:var(--brand); color:var(--brand); background:var(--brand-soft); font-weight:500; }
  .yuntu-wb .seg.disabled { opacity:.45; pointer-events:none; }
  .yuntu-wb .label-cb { display:inline-flex; align-items:center; gap:6px; cursor:pointer; user-select:none; }
  .yuntu-wb .label-cb .box { width:14px; height:14px; border:1.5px solid var(--border); border-radius:4px; display:inline-flex; align-items:center; justify-content:center;
    font-size:10px; color:#fff; transition:.14s; background:#fff; }
  .yuntu-wb .label-cb.on .box { background:var(--brand); border-color:var(--brand); }
  .yuntu-wb .label-cb:hover .box { border-color:var(--brand-line); }
  .yuntu-wb .prompt-box { border:1px solid var(--border); border-radius:12px; background:#fff; overflow:hidden; box-shadow:0 1px 2px rgba(216,90,48,.05),0 4px 16px rgba(216,90,48,.05); }
  .yuntu-wb .prompt-box textarea { width:100%; min-height:92px; border:none; outline:none; padding:15px 16px; font-size:14px; font-family:inherit; resize:vertical; color:var(--text); background:transparent; line-height:1.7; }
  .yuntu-wb .prompt-toolbar { display:flex; align-items:center; gap:10px; padding:9px 13px; border-top:1px solid var(--border); background:#FEFCFB; }
  .yuntu-wb .tool { color:var(--text-3); cursor:pointer; font-size:13px; padding:3px 7px; border-radius:6px; transition:.14s; }
  .yuntu-wb .tool:hover { color:var(--brand); background:var(--brand-soft); }
  .yuntu-wb .btn { display:inline-flex; align-items:center; gap:6px; padding:9px 22px; border-radius:9px; border:none; cursor:pointer; font-size:14px; font-weight:500; transition:.16s; font-family:inherit; }
  .yuntu-wb .btn-primary { background:linear-gradient(135deg,#E0693F,#D85A30); color:#fff; box-shadow:0 4px 12px rgba(216,90,48,.28); }
  .yuntu-wb .btn-primary:hover { background:linear-gradient(135deg,#C9552A,#C14F28); transform:translateY(-1px); }
  .yuntu-wb .btn-primary:disabled { opacity:.6; cursor:not-allowed; transform:none; }
  .yuntu-wb .btn-ghost { background:#fff; color:var(--text-2); border:1px solid var(--border); }
  .yuntu-wb .btn-ghost:hover { border-color:var(--brand-line); color:var(--brand); }
  .yuntu-wb .result-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:13px; }
  .yuntu-wb .result-head b { font-size:14px; }
  .yuntu-wb .result-head .meta { font-size:12px; color:var(--text-3); }
  .yuntu-wb .gen-progress { display:flex; align-items:center; gap:10px; margin:-3px 0 12px; }
  .yuntu-wb .gen-progress-bar { flex:1; height:5px; border-radius:99px; background:var(--brand-soft); overflow:hidden; }
  .yuntu-wb .gen-progress-fill { height:100%; border-radius:99px; background:linear-gradient(90deg,#F3A98B,var(--brand)); transition:width .6s ease; }
  .yuntu-wb .gen-progress-txt { font-size:12px; color:var(--brand); min-width:42px; text-align:right; font-variant-numeric:tabular-nums; }
  .yuntu-wb .grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
  .yuntu-wb .cell { border:1px solid var(--border); border-radius:10px; overflow:hidden; background:#fff; transition:.18s; }
  .yuntu-wb .cell:hover { border-color:var(--brand-line); box-shadow:0 6px 18px rgba(216,90,48,.10); }
  .yuntu-wb .cell .img { aspect-ratio:1/1; background:linear-gradient(135deg,#FBF1EC,#EAF3F8); position:relative; overflow:hidden; display:flex; align-items:center; justify-content:center; }
  .yuntu-wb .cell .img img { width:100%; height:100%; object-fit:cover; display:block; }
  .yuntu-wb .cell .ops { display:flex; gap:4px; padding:7px; border-top:1px solid var(--border); }
  .yuntu-wb .cell .ops span { flex:1; text-align:center; font-size:12px; color:var(--text-2); padding:4px 0; border-radius:6px; cursor:pointer; transition:.13s; display:inline-flex; align-items:center; justify-content:center; gap:3px; }
  .yuntu-wb .cell .ops span:hover { background:var(--brand-soft); color:var(--brand); }
  .yuntu-wb .cell .ops span.disabled { opacity:.4; cursor:not-allowed; }
  .yuntu-wb .empty-hint { text-align:center; color:var(--text-3); padding:36px 0; font-size:13px; }
  .yuntu-wb .bg-box { display:flex; flex-direction:column; gap:7px; }
  .yuntu-wb .bg-row { display:flex; align-items:flex-start; gap:8px; font-size:12px; line-height:1.5; }
  .yuntu-wb .bg-k { color:var(--text-3); flex-shrink:0; width:34px; }
  .yuntu-wb .bg-v { color:var(--text); flex:1; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .yuntu-wb .swatch { width:14px; height:14px; border-radius:4px; border:1px solid var(--border); display:inline-block; flex-shrink:0; }
  .yuntu-wb .bg-quick { display:flex; flex-wrap:wrap; gap:6px; margin-top:2px; }
  .yuntu-wb .bg-chip { font-size:12px; padding:4px 10px; border-radius:20px; background:var(--blue); color:#1F5C99; border:1px solid #CBE0EC; cursor:pointer; transition:.15s; display:inline-flex; align-items:center; gap:3px; }
  .yuntu-wb .bg-chip:hover { background:#D8EAF4; }
  .yuntu-wb .bg-tpls { display:flex; flex-direction:column; gap:6px; }
  .yuntu-wb .bg-tpl { display:flex; align-items:center; gap:7px; padding:5px 6px; border:1px solid var(--border); border-radius:8px; }
  .yuntu-wb .bg-tpl img { width:28px; height:28px; border-radius:5px; object-fit:cover; flex-shrink:0; }
  .yuntu-wb .bg-tpl .nm { flex:1; font-size:12px; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .yuntu-wb .bg-tpl .act { font-size:12px; color:var(--brand); cursor:pointer; flex-shrink:0; }
  .yuntu-wb .bg-tpl .act.del { color:var(--text-3); }
  .yuntu-wb .bg-tpl .act.del:hover { color:#C14F28; }
`;

function Seg({ options, value, onChange }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <span key={o.value} className={'opt' + (o.value === value ? ' active' : '')} onClick={() => onChange(o.value)}>{o.label}</span>
      ))}
    </div>
  );
}

export default function ImageWorkbench() {
  const [productUrls, setProductUrls] = useState([]); // 多张产品图（批量出图）
  const [activeProduct, setActiveProduct] = useState(0); // 当前选中产品图索引
  const [sellingPoints, setSellingPoints] = useState([]);
  const [recognizing, setRecognizing] = useState(false);
  const [refs, setRefs] = useState([]);
  const [ratio, setRatio] = useState('1:1');
  const [resol, setResol] = useState('1k'); // 输出分辨率：1k / 2k
  const [count, setCount] = useState(1);
  const [style, setStyle] = useState('white');
  const [useStyle, setUseStyle] = useState(false); // 风格为可选项：勾选才生效
  const [prompt, setPrompt] = useState('');
  const [results, setResults] = useState([]);
  const [lastMeta, setLastMeta] = useState(null); // { taskType, selfCheck } 本轮任务类型 + 自检结果
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0); // 生成进度条（0=隐藏，100=完成）
  const progressTimerRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyImgs, setHistoryImgs] = useState([]);
  const [canvasInit, setCanvasInit] = useState(null); // { cleanBase, textRegions, sourceUrl } 画布改字目标
  const [canvasLoading, setCanvasLoading] = useState(false); // 画布初始化中
  const [bgInfo, setBgInfo] = useState(null); // 背景识别结果 { background, material, lighting, color, composition, props, spell, imagePrompt }
  const [bgRecognizing, setBgRecognizing] = useState(false); // 背景识别中
  const [bgVarPrompt, setBgVarPrompt] = useState(''); // 随机变化变体提示词（同系列但明显不同）
  const [bgVarying, setBgVarying] = useState(false); // 变体生成中
  const [bgTemplates, setBgTemplates] = useState([]); // 背景模板库
  const [bgSaveOpen, setBgSaveOpen] = useState(false); // 存模板弹窗
  const [bgSaveName, setBgSaveName] = useState(''); // 模板名
  const [savedPrompts, setSavedPrompts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SAVED_PROMPTS_KEY) || '[]'); } catch (e) { return []; }
  }); // 收藏的常用提示词
  const [savePromptOpen, setSavePromptOpen] = useState(false); // 收藏弹窗
  const [savePromptName, setSavePromptName] = useState(''); // 收藏名

  const productInputRef = useRef(null);
  const refInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceIndexRef = useRef(-1);
  const productUrl = productUrls[activeProduct] || null; // 派生：当前选中产品图

  const uploadBase64 = async (base64) => {
    const res = await apiFetch('/api/upload/image', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileBase64: base64 }),
    }).then((r) => r.json());
    return res;
  };

  // 上传产品图（支持多张：一次多选，追加到列表；不自动识别——卖点/背景识别均手动触发）
  const handleProductFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const ok = [];
    for (const file of list) {
      try {
        const base64 = await readFileBase64(file);
        const res = await uploadBase64(base64);
        if (res && res.success && res.url) ok.push(res.url);
      } catch (e) { /* 单张失败跳过，继续传下一张 */ }
    }
    if (!ok.length) { message.error('上传失败'); return; }
    const room = Math.max(0, 9 - productUrls.length);
    if (ok.length > room) message.warning('最多 9 张产品图，超出部分已忽略');
    setProductUrls((prev) => [...prev, ...ok].slice(0, 9));
    setSellingPoints([]);
    setBgInfo(null);
    setBgVarPrompt('');
    message.success('已添加 ' + Math.min(ok.length, room) + ' 张产品图');
  };

  // 删除单张产品图（active 索引随删除位置校正）
  const removeProduct = (i) => {
    setProductUrls((prev) => prev.filter((_, j) => j !== i));
    setActiveProduct((a) => {
      if (i < a) return a - 1;
      if (i === a) return Math.min(a, Math.max(0, productUrls.length - 2));
      return a;
    });
  };

  // 替换单张产品图（点缩略图右下角 ↻）
  const handleReplaceFile = async (file) => {
    const i = replaceIndexRef.current;
    replaceIndexRef.current = -1;
    if (!file || i < 0) return;
    try {
      const base64 = await readFileBase64(file);
      const res = await uploadBase64(base64);
      if (res && res.success && res.url) {
        setProductUrls((prev) => prev.map((u, j) => (j === i ? res.url : u)));
        if (i === activeProduct) { setSellingPoints([]); setBgInfo(null); setBgVarPrompt(''); }
      } else { message.error((res && res.error) || '上传失败'); }
    } catch (e) { message.error('上传失败'); }
  };

  // 识别卖点
  const recognize = async (url) => {
    setRecognizing(true);
    try {
      const r = await apiFetch('/api/extract-selling-points', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: url }),
      }).then((r) => r.json());
      if (r.success && r.data) {
        const pts = (r.data.sellingPoints || []).map((p) => (typeof p === 'string' ? p : (p.text || p.point || p.name || ''))).filter(Boolean);
        setSellingPoints(pts);
        message.success('卖点识别完成');
      } else {
        message.warning(r.error || '卖点识别失败，可手动补充');
        setSellingPoints([]);
      }
    } catch (e) { message.warning('卖点识别失败'); setSellingPoints([]); }
    setRecognizing(false);
  };

  // 识别背景（素材图 → 背景模板五要素 + 模板咒语）
  const recognizeBackground = async (url) => {
    setBgRecognizing(true);
    setBgInfo(null);
    try {
      const r = await apiFetch('/api/yuntu/workbench/analyze-background', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: url }),
      }).then((r) => r.json());
      if (r.success && r.data) { setBgInfo(r.data); setBgVarPrompt(''); message.success('背景识别完成'); }
      else { message.warning(r.error || '背景识别失败'); setBgInfo(null); setBgVarPrompt(''); }
    } catch (e) { message.warning('背景识别失败'); setBgInfo(null); }
    setBgRecognizing(false);
  };

  // 复制整图提示词到剪贴板（https 下走 clipboard API，兜底 execCommand）
  const copyBgPrompt = async (text) => {
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('整图提示词已复制');
    } catch (e) { message.warning('复制失败，请手动选中复制'); }
  };

  // 随机变化：基于当前整图提示词生成「同系列但明显不同」的变体（AI 改写，幅度大）
  const varyBgPrompt = async () => {
    const base = bgInfo && bgInfo.imagePrompt;
    if (!base) return message.warning('请先识别背景');
    setBgVarying(true);
    try {
      const r = await apiFetch('/api/yuntu/workbench/vary-background-prompt', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: base, previous: bgVarPrompt || '' }),
      }).then((r) => r.json());
      if (r.success && r.data && r.data.prompt) { setBgVarPrompt(r.data.prompt); message.success('已生成新变体'); }
      else message.warning(r.error || '变体生成失败');
    } catch (e) { message.warning('变体生成失败'); }
    setBgVarying(false);
  };

  // 快捷改背景：点击 chip → 精准指令填入输入框
  const applyBgChange = (target) => {    const map = {
      warm: '把图中产品的背景换成暖白色，产品形状、颜色、材质、构图完全保持不变，背景光影柔和自然',
      white: '把背景换成纯白棚拍背景（干净白底、柔和棚拍光影、产品底部自然接触阴影），产品保持不变',
      wood: '把背景换成暖色木纹桌面场景（浅木色、直纹哑光、自然光影），产品保持不变并落地',
      marble: '把背景换成浅色大理石纹理背景（细腻纹路、哑光、淡雅），产品保持不变',
    };
    const known = map[target];
    if (known) { setPrompt(known); return; }
    // 自定义颜色
    const color = (target || '').trim();
    if (!color) return;
    setPrompt('把图中产品的背景换成' + color + '，产品形状、颜色、材质、构图完全保持不变');
  };

  // 加载背景模板库
  const loadBgTemplates = async () => {
    try {
      const r = await apiFetch('/api/background-templates').then((r) => r.json());
      setBgTemplates((r && r.data) || []);
    } catch (e) { setBgTemplates([]); }
  };
  useEffect(() => { loadBgTemplates(); }, []);

  // 存为背景模板
  const saveBgTemplate = async () => {
    const name = bgSaveName.trim();
    if (!name) return message.warning('请输入模板名');
    try {
      const r = await apiFetch('/api/background-templates', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, thumb: productUrl, result: bgInfo }),
      }).then((r) => r.json());
      if (r.success) { message.success('已存为背景模板'); setBgSaveOpen(false); setBgSaveName(''); loadBgTemplates(); }
      else message.error(r.error || '保存失败');
    } catch (e) { message.error('保存失败'); }
  };

  // 应用背景模板：把模板咒语拼成换背景指令
  const applyBgTemplate = (t) => {
    const spell = (t.result && t.result.spell) || '';
    if (!spell) return message.warning('该模板缺少背景描述');
    setPrompt('把图中产品的背景替换为以下背景：' + spell + '；产品形状、颜色、材质、构图完全保持不变');
  };

  // 删除背景模板
  const deleteBgTemplate = async (id) => {
    try {
      await apiFetch('/api/background-templates/' + id, { method: 'DELETE' }).then((r) => r.json());
      loadBgTemplates();
    } catch (e) { /* 忽略 */ }
  };

  // 收藏当前提示词（localStorage 持久化，按内容去重）
  const savePrompt = () => {
    const text = prompt.trim();
    if (!text) return message.warning('请先在输入框写好提示词');
    if (savedPrompts.some((p) => p.prompt === text)) return message.warning('这条提示词已收藏过');
    const item = { id: Date.now(), name: savePromptName.trim() || text.slice(0, 16), prompt: text };
    const next = [...savedPrompts, item];
    setSavedPrompts(next);
    try { localStorage.setItem(SAVED_PROMPTS_KEY, JSON.stringify(next)); } catch (e) {}
    setSavePromptOpen(false);
    setSavePromptName('');
    message.success('已收藏提示词');
  };
  const removeSavedPrompt = (id) => {
    const next = savedPrompts.filter((p) => p.id !== id);
    setSavedPrompts(next);
    try { localStorage.setItem(SAVED_PROMPTS_KEY, JSON.stringify(next)); } catch (e) {}
  };

  // 上传参考图
  const handleRefFile = async (file) => {
    if (!file) return;
    try {
      const base64 = await readFileBase64(file);
      const res = await uploadBase64(base64);
      if (res && res.success && res.url) { setRefs((prev) => [...prev, res.url].slice(0, 3)); message.success('参考图已添加'); }
      else message.error((res && res.error) || '上传失败');
    } catch (e) { message.error('上传失败'); }
  };

  // 生成主图：单张产品图 = 原流程；多张产品图 = 批量出图（逐张串行，结果合并）
  // onlyActive=true（批量模式下点结果图「重绘」）只重绘当前选中产品图，避免全量重跑
  const doGenerate = async (genCount, append = false, onlyActive = false) => {
    if (!prompt.trim()) return message.warning('请输入主图需求描述');
    if (!productUrls.length) return message.warning('请先上传产品图');
    setGenerating(true);
    setGenProgress(5);
    const targets = onlyActive && productUrl ? [productUrl] : productUrls;
    const batch = targets.length > 1;
    // 单张：模拟进度定时器；批量：按完成张数推进
    if (!batch) {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      progressTimerRef.current = setInterval(() => {
        setGenProgress((p) => {
          if (p < 50) return p + Math.random() * 7 + 3;
          if (p < 80) return p + Math.random() * 3 + 1;
          if (p < 92) return p + Math.random() * 1.2 + 0.3;
          return p;
        });
      }, 600);
    }
    const all = [];
    let meta = null;
    let okCount = 0, failCount = 0;
    try {
      for (let i = 0; i < targets.length; i++) {
        const pu = targets[i];
        const res = await apiFetch('/api/yuntu/workbench/generate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt, imageUrls: [pu, ...refs].slice(0, 3),
            size: resol === '2k' ? (RATIO_SIZE_2K[ratio] || '2048x2048') : (RATIO_SIZE[ratio] || '1024x1024'),
            composeSize: resol === '2k' ? '2048*2048' : '1024*1024',
            count: genCount,
            sellingPoints: batch ? [] : sellingPoints, // 批量时不共用单张卖点，各产品由模型自行提炼
            style: useStyle ? style : null,
          }),
        }).then((r) => r.json());
        if (res.success && res.data) {
          const urls = (res.data.urls || []).map((u) => ({ url: u.url || u, thumbnail: u.thumbnail || u.url || u }));
          if (urls.length) {
            all.push(...urls);
            okCount++;
            meta = { taskType: res.data.taskType || null, selfCheck: res.data.selfCheck || null, top3: res.data.top3 || null, promptPack: res.data.promptPack || null };
          } else {
            failCount++;
            meta = { taskType: res.data.taskType || null, selfCheck: res.data.selfCheck || null, top3: res.data.top3 || null, promptPack: res.data.promptPack || null };
          }
        } else {
          failCount++;
        }
        if (batch) setGenProgress(Math.round(5 + ((i + 1) / targets.length) * 87));
      }
    } catch (e) { message.error('生成失败'); }
    if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
    setGenProgress(100);
    setTimeout(() => setGenProgress(0), 900); // 满条停留片刻再隐藏
    setGenerating(false);
    if (all.length) {
      setResults((prev) => (append ? [...prev, ...all] : all));
      if (batch) {
        setLastMeta({ ...(meta || {}), selfCheck: '批量 ' + okCount + '/' + targets.length + ' 张产品图' + (failCount ? '（' + failCount + ' 张失败）' : '') });
      } else {
        setLastMeta(meta);
      }
      message[failCount ? 'warning' : 'success']('生成成功 ' + all.length + ' 张' + (failCount ? '，' + failCount + ' 张失败' : ''));
    } else {
      message.error('生成失败，请重试');
    }
  };

  // 下载
  const download = (url, name) => {
    const a = document.createElement('a');
    a.href = url; a.download = name || 'image'; a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // 改字：结果图 → 后端去字干净底图 + 文字区域清单 → 打开 Fabric 画布编辑
  const openCanvasEditor = async (url) => {
    setCanvasLoading(true);
    try {
      const res = await apiFetch('/api/yuntu/workbench/canvas-init', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: url }),
      }).then((r) => r.json());
      if (res.success && res.data) {
        setCanvasInit({
          cleanBase: res.data.cleanBase,
          textRegions: res.data.textRegions || [],
          subject: res.data.subject || null,
          baseHasText: !!res.data.baseHasText,
          sourceUrl: url,
        });
      } else {
        message.error(res.error || '画布初始化失败');
      }
    } catch (e) { message.error('画布初始化失败'); }
    setCanvasLoading(false);
  };

  // 画布保存：用新图替换原结果格子；保存后画布不关闭，可继续增删文字
  const onCanvasSaved = (newUrl) => {
    setResults((prev) => prev.map((r) => (r.url === canvasInit.sourceUrl ? { ...r, url: newUrl, thumbnail: newUrl } : r)));
    // 不关闭画布，仅更新 sourceUrl，下次保存仍能定位到同一结果格
    setCanvasInit((prev) => (prev ? { ...prev, sourceUrl: newUrl } : prev));
  };

  // 历史记录
  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryImgs([]);
    try {
      const r = await apiFetch('/api/image-history').then((r) => r.json());
      const list = (r && r.data) || [];
      const imgs = [];
      list.forEach((item) => {
        const us = item.urls || [];
        us.forEach((u) => { if (u && (u.url || typeof u === 'string')) imgs.push(u.url || u); });
      });
      setHistoryImgs(imgs.slice(0, 60));
    } catch (e) { setHistoryImgs([]); }
  };

  // ===== 九宫格拼接（3×3，纯本地合成零费用）=====
  const [gridOpen, setGridOpen] = useState(false);
  const [gridImgs, setGridImgs] = useState(Array(9).fill(''));   // 9 个槽位的图片 url
  const [gridResult, setGridResult] = useState('');              // 合成结果
  const [gridBusy, setGridBusy] = useState(false);
  const [gridShowIndex, setGridShowIndex] = useState(true);      // 左下角编号 01-09
  const [gridGap, setGridGap] = useState(true);                  // 白色缝隙
  const [gridCell, setGridCell] = useState(1000);                // 每格分辨率
  const [gridPickerOpen, setGridPickerOpen] = useState(false);   // 历史选图弹窗
  const gridFileRef = useRef(null);
  const gridSlotRef = useRef(-1);                                // 本地上传目标槽位
  const dragGridRef = useRef(-1);                                // 拖拽换序源槽位

  const openGridModal = () => {
    setGridResult('');
    // 预填当前生成结果（最多 9 张），不足的留空
    const pre = results.slice(0, 9).map((r) => r.url);
    setGridImgs(Array(9).fill('').map((_, i) => pre[i] || ''));
    setGridOpen(true);
  };

  const fillGridFromHistory = async () => {
    setHistoryImgs([]);
    try {
      const r = await apiFetch('/api/image-history').then((r) => r.json());
      const list = (r && r.data) || [];
      const imgs = [];
      list.forEach((item) => {
        const us = item.urls || [];
        us.forEach((u) => { if (u && (u.url || typeof u === 'string')) imgs.push(u.url || u); });
      });
      setHistoryImgs(imgs.slice(0, 60));
      setGridPickerOpen(true);
    } catch (e) { message.error('历史作品加载失败'); }
  };

  // 历史选图：填入第一个空槽（指定槽位时填入指定槽）
  const pickFromHistory = (url) => {
    setGridImgs((prev) => {
      const next = [...prev];
      const slot = gridSlotRef.current >= 0 ? gridSlotRef.current : next.indexOf('');
      if (slot >= 0 && slot < 9) next[slot] = url;
      return next;
    });
    gridSlotRef.current = -1;
    setGridPickerOpen(false);
  };

  // 槽位点击：空格 → 本地上传；已填 → 打开历史选择器替换
  const onGridSlotClick = (i) => {
    if (!gridImgs[i]) {
      gridSlotRef.current = i;
      gridFileRef.current && gridFileRef.current.click();
    } else {
      gridSlotRef.current = i;
      fillGridFromHistory();
    }
  };

  const handleGridFile = async (file) => {
    const slot = gridSlotRef.current;
    gridSlotRef.current = -1;
    if (!file || slot < 0) return;
    try {
      const base64 = await readFileBase64(file);
      const res = await uploadBase64(base64);
      if (res && res.success && res.url) {
        setGridImgs((prev) => { const n = [...prev]; n[slot] = res.url; return n; });
      } else message.error((res && res.error) || '上传失败');
    } catch (e) { message.error('上传失败'); }
  };

  // 拖拽换序
  const onGridDrop = (i) => {
    const from = dragGridRef.current;
    dragGridRef.current = -1;
    if (from < 0 || from === i) return;
    setGridImgs((prev) => { const n = [...prev]; [n[from], n[i]] = [n[i], n[from]]; return n; });
  };

  const doGridCompose = async () => {
    if (gridImgs.filter(Boolean).length < 9) return message.warning('请先填满 9 张图片（当前 ' + gridImgs.filter(Boolean).length + ' 张）');
    setGridBusy(true);
    setGridResult('');
    try {
      const res = await apiFetch('/api/yuntu/grid-compose', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: gridImgs, cellSize: gridCell, gap: gridGap ? 12 : 0, showIndex: gridShowIndex }),
      }).then((r) => r.json());
      if (res && res.success && res.url) {
        setGridResult(res.url);
        message.success('九宫格已生成');
      } else message.error((res && res.error) || '拼接失败');
    } catch (e) { message.error('拼接失败'); }
    setGridBusy(false);
  };


  return (
    <div className="yuntu-wb">
      <style>{css}</style>

      {/* 顶部栏 */}
      <header className="topbar">
        <div className="logo"><span className="cloud">☁</span>AI云图 <span className="sub">AI 电商生图</span></div>
        <div className="spacer"></div>
        <div className="model-select" onClick={() => message.info('当前模型：qwen-image-3.0-pro（旗舰）')}>
          <b>qwen-image-3.0-pro</b><span className="tag">旗舰</span><span style={{ color: '#B9A79E', fontSize: 11 }}>▾</span>
        </div>
        <button className="icon-btn" title="历史记录" onClick={openHistory}><HistoryOutlined /></button>
        <button className="icon-btn" title="设置" onClick={() => message.info('设置功能即将上线')}><SettingOutlined /></button>
      </header>

      <div className="layout">
        {/* 左侧参数面板 */}
        <aside className="sidebar">
          <div className="card">
            <h3>产品图 {productUrls.length > 0 && <span className="link" onClick={() => productInputRef.current && productInputRef.current.click()}>＋ 添加</span>}</h3>
            {productUrls.length ? (
              <div className="product-grid">
                {productUrls.map((u, i) => (
                  <div className={'pcell' + (i === activeProduct ? ' active' : '')} key={u} onClick={() => setActiveProduct(i)}>
                    <img src={u} alt="" referrerPolicy="no-referrer" />
                    {i === activeProduct && <span className="pbadge">当前</span>}
                    <span className="pdel" title="删除" onClick={(e) => { e.stopPropagation(); removeProduct(i); }}>×</span>
                    <span className="prep" title="替换这张" onClick={(e) => { e.stopPropagation(); replaceIndexRef.current = i; replaceInputRef.current && replaceInputRef.current.click(); }}>↻</span>
                  </div>
                ))}
                {productUrls.length < 9 && <div className="pcell add" onClick={() => productInputRef.current && productInputRef.current.click()}>＋</div>}
              </div>
            ) : (
              <div className="upload" onClick={() => productInputRef.current && productInputRef.current.click()}>
                <div className="plus">＋</div>
                <div>上传产品白底图</div>
                <div className="sub">支持多张（一次可多选）· JPG / PNG · 建议 800×800 以上</div>
              </div>
            )}
            <input ref={productInputRef} type="file" accept="image/*" multiple hidden
              onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; handleProductFiles(fs); }} />
            <input ref={replaceInputRef} type="file" accept="image/*" hidden
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; handleReplaceFile(f); }} />
          </div>

          <div className="card">
            <h3>卖点识别 <span className="link" onClick={() => productUrl && recognize(productUrl)}>识别卖点</span></h3>
            {recognizing ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: '#B9A79E' }}><Spin size="small" /> 识别中…</div>
            ) : sellingPoints.length ? (
              <div className="tags">
                {sellingPoints.map((p, i) => (
                  <span key={i} className="tag-chip" onClick={() => setSellingPoints((prev) => prev.filter((_, j) => j !== i))}>{p}</span>
                ))}
              </div>
            ) : (
              <div className="tags"><span className="tag-chip off">上传产品图后点「识别卖点」</span></div>
            )}
          </div>

          <div className="card">
            <h3>背景识别 <span className="link" onClick={() => productUrl && recognizeBackground(productUrl)}>识别背景</span></h3>
            {bgRecognizing ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: '#B9A79E' }}><Spin size="small" /> 识别中…</div>
            ) : bgInfo ? (
              <div className="bg-box">
                {bgInfo.background && bgInfo.background['类型'] && bgInfo.background['类型'] !== '无' && (
                  <div className="bg-row">
                    <span className="bg-k">背景</span>
                    <span className="bg-v">{bgInfo.background['类型']}{bgInfo.background['描述'] ? ' · ' + bgInfo.background['描述'] : ''}</span>
                  </div>
                )}
                {bgInfo.color && bgInfo.color['主色'] && (
                  <div className="bg-row">
                    <span className="bg-k">主色</span>
                    <span className="bg-v"><span className="swatch" style={{ background: bgInfo.color['主色'] }}></span>{bgInfo.color['主色']}</span>
                  </div>
                )}
                {bgInfo.material && bgInfo.material['类型'] && bgInfo.material['类型'] !== '无' && (
                  <div className="bg-row">
                    <span className="bg-k">材质</span>
                    <span className="bg-v">{bgInfo.material['类型']}{bgInfo.material['颜色'] && bgInfo.material['颜色'] !== '无' ? ' · ' + bgInfo.material['颜色'] : ''}</span>
                  </div>
                )}
                {bgInfo.lighting && bgInfo.lighting['方向'] && bgInfo.lighting['方向'] !== '无' && (
                  <div className="bg-row">
                    <span className="bg-k">光影</span>
                    <span className="bg-v">{bgInfo.lighting['方向']}{bgInfo.lighting['强度'] && bgInfo.lighting['强度'] !== '无' ? ' · ' + bgInfo.lighting['强度'] : ''}</span>
                  </div>
                )}
                {bgInfo.props && bgInfo.props.length > 0 && (
                  <div className="bg-row"><span className="bg-k">点缀</span><span className="bg-v">{bgInfo.props.join('、')}</span></div>
                )}
                {bgInfo.imagePrompt && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#B9A79E', marginBottom: 4 }}>整图提示词（AI 识别背景素材 → 生成整张图的提示词）</div>
                    <div style={{ background: '#FFF9F6', border: '1px solid #F0E2DB', borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.7, color: '#33251F', maxHeight: 130, overflow: 'auto', userSelect: 'text' }}>{bgInfo.imagePrompt}</div>
                    <div className="bg-quick" style={{ marginTop: 6 }}>
                      <span className="bg-chip" onClick={() => copyBgPrompt(bgInfo.imagePrompt)}>复制提示词</span>
                      <span className="bg-chip" onClick={() => setPrompt(bgInfo.imagePrompt)}>填入输入框</span>
                      <span className="bg-chip" onClick={() => !bgVarying && varyBgPrompt()} style={bgVarying ? { opacity: 0.6 } : null}>
                        {bgVarying ? <Spin size="small" /> : '🎲'} 随机变化
                      </span>
                    </div>
                    {bgVarPrompt && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 12, color: '#5B8DB8', marginBottom: 4 }}>🎲 同系列变体（风格相近、要素大不同，可反复点出新的）</div>
                        <div style={{ background: '#EAF3F8', border: '1px solid #D3E5F0', borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.7, color: '#26343F', maxHeight: 130, overflow: 'auto', userSelect: 'text' }}>{bgVarPrompt}</div>
                        <div className="bg-quick" style={{ marginTop: 6 }}>
                          <span className="bg-chip" onClick={() => !bgVarying && varyBgPrompt()} style={bgVarying ? { opacity: 0.6 } : null}>{bgVarying ? '生成中…' : '再来一个'}</span>
                          <span className="bg-chip" onClick={() => copyBgPrompt(bgVarPrompt)}>复制</span>
                          <span className="bg-chip" onClick={() => setPrompt(bgVarPrompt)}>填入输入框</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="bg-quick">
                  <span className="bg-chip" onClick={() => applyBgChange('warm')}>换暖白</span>
                  <span className="bg-chip" onClick={() => applyBgChange('white')}>换纯白棚拍</span>
                  <span className="bg-chip" onClick={() => applyBgChange('wood')}>换木纹</span>
                  <span className="bg-chip" onClick={() => applyBgChange('marble')}>换大理石</span>
                </div>
                <div className="bg-quick" style={{ marginTop: 6 }}>
                  <span className="bg-chip" onClick={() => setBgSaveOpen(true)}><SaveOutlined /> 存为背景模板</span>
                </div>
              </div>
            ) : (
              <div className="tags"><span className="tag-chip off">上传产品图后点「识别背景」</span></div>
            )}
          </div>

          {bgTemplates.length > 0 && (
            <div className="card">
              <h3>背景模板库</h3>
              <div className="bg-tpls">
                {bgTemplates.map((t) => (
                  <div className="bg-tpl" key={t.id}>
                    {t.thumb ? <img src={t.thumb} alt="" referrerPolicy="no-referrer" /> : null}
                    <span className="nm" title={t.name}>{t.name}</span>
                    <span className="act" onClick={() => applyBgTemplate(t)}>应用</span>
                    <span className="act del" onClick={() => deleteBgTemplate(t.id)}><DeleteOutlined /></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <h3>参考图 <span className="link" onClick={() => refInputRef.current && refInputRef.current.click()}>＋ 添加</span></h3>
            <div className="refs">
              {refs.map((r, i) => (
                <div className="ref" key={r}>
                  <img src={r} alt="" referrerPolicy="no-referrer" />
                  <span className="del" onClick={() => setRefs((prev) => prev.filter((_, j) => j !== i))}>×</span>
                </div>
              ))}
              {refs.length < 3 && <div className="ref add" onClick={() => refInputRef.current && refInputRef.current.click()}>＋</div>}
            </div>
            <input ref={refInputRef} type="file" accept="image/*" hidden
              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleRefFile(f); e.target.value = ''; }} />
          </div>

          <div className="card">
            <h3>生成参数</h3>
            <div className="param-row">
              <span className="label">画布比例</span>
              <Seg options={RATIOS} value={ratio} onChange={setRatio} />
            </div>
            <div className="param-row">
              <span className="label">输出分辨率</span>
              <Seg options={[{ value: '1k', label: '1K' }, { value: '2k', label: '2K' }]} value={resol} onChange={setResol} />
            </div>
            <div className="param-row" style={{ fontSize: 11, color: '#B9A79E', marginTop: -4 }}>
              {resol === '2k' ? '2K（2048×2048）· 输出 0.5 元/张' : '1K（1024×1024）· 输出 0.25 元/张'}
            </div>
            <div className="param-row">
              <span className="label">生成数量</span>
              <Seg options={COUNTS.map((c) => ({ value: c, label: String(c) }))} value={count} onChange={setCount} />
            </div>
            <div className="param-row">
              <span className="label">
                <span className={'label-cb' + (useStyle ? ' on' : '')} onClick={() => setUseStyle((v) => !v)}>
                  <span className="box">{useStyle ? '✓' : ''}</span>风格（可选，勾选后生效）
                </span>
              </span>
              <div className={'seg' + (useStyle ? '' : ' disabled')}>
                {STYLES.map((o) => (
                  <span key={o.value} className={'opt' + (o.value === style ? ' active' : '')} onClick={() => setStyle(o.value)}>{o.label}</span>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* 右侧主区 */}
        <main className="main">
          <div className="prompt-box">
            <textarea
              placeholder="描述你想要的主图，例如：酒店一次性牙刷主图，小麦秸秆环保材质，黑色软毛，白底简约风，突出「天然可降解」卖点……"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div className="prompt-toolbar">
              <span className="tool" onClick={() => refInputRef.current && refInputRef.current.click()}>🖼 参考图</span>
              <span className="tool" onClick={() => productInputRef.current && productInputRef.current.click()}>📷 传图</span>
              <span className="tool" onClick={() => message.info('链接功能即将上线')}>🔗 链接</span>
              <span className="tool" onClick={openGridModal}>▦ 九宫格</span>
              <div className="spacer" style={{ flex: 1 }}></div>
              <button className="btn btn-ghost" onClick={() => { setPrompt(''); setResults([]); setLastMeta(null); }}>清空</button>
              <button className="btn btn-ghost" onClick={() => { if (!prompt.trim()) return message.warning('请先写提示词'); setSavePromptName(''); setSavePromptOpen(true); }}>⭐ 收藏</button>
              <Dropdown
                trigger={['click']}
                placement="topLeft"
                menu={{
                  items: [
                    ...PROMPT_TEMPLATES.map((t) => ({
                      key: t.key,
                      label: (
                        <div style={{ minWidth: 260, padding: '2px 0' }}>
                          <div style={{ fontWeight: 600, color: '#33251F' }}>{t.label}</div>
                          <div style={{ fontSize: 12, color: '#8A7A72' }}>{t.desc}</div>
                        </div>
                      ),
                    })),
                    ...(savedPrompts.length
                      ? [
                          { type: 'divider' },
                          { key: '__saved_head', label: <span style={{ fontSize: 12, fontWeight: 600, color: '#B98A6B' }}>⭐ 我的收藏</span>, disabled: true },
                          ...savedPrompts.map((p) => ({
                            key: 'saved_' + p.id,
                            label: (
                              <div style={{ minWidth: 260, padding: '2px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, color: '#33251F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                                  <div style={{ fontSize: 12, color: '#8A7A72', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.prompt}</div>
                                </div>
                                <span
                                  style={{ flexShrink: 0, color: '#C9A58F', fontSize: 12, padding: '2px 6px' }}
                                  onClick={(e) => { e.stopPropagation(); removeSavedPrompt(p.id); }}
                                >删除</span>
                              </div>
                            ),
                          })),
                        ]
                      : []),
                  ],
                  onClick: ({ key }) => {
                    if (String(key).indexOf('saved_') === 0) {
                      const id = Number(String(key).slice(6));
                      const p = savedPrompts.find((x) => x.id === id);
                      if (p) { setPrompt(p.prompt); message.success('已填入收藏「' + p.name + '」'); }
                      return;
                    }
                    const t = PROMPT_TEMPLATES.find((x) => x.key === key);
                    if (t) { setPrompt(t.prompt); message.success('已填入「' + t.label + '」，可继续编辑'); }
                  },
                }}
              >
                <button className="btn btn-ghost" type="button">📝 创作指令词</button>
              </Dropdown>
              <button className="btn btn-primary" disabled={generating} onClick={() => doGenerate(count, false)}>
                {generating ? '生成中…' : '✦ 生成主图'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="result-head">
              <b>生成结果</b>
              <span className="meta">qwen-image-3.0-pro · {results.length || count} 张</span>
            </div>
            {(generating || genProgress > 0) && (
              <div className="gen-progress">
                <div className="gen-progress-bar">
                  <div className="gen-progress-fill" style={{ width: Math.min(genProgress, 100) + '%' }} />
                </div>
                <span className="gen-progress-txt">{genProgress >= 100 ? '✓ 完成' : Math.floor(genProgress) + '%'}</span>
              </div>
            )}
            {lastMeta && (lastMeta.taskType || lastMeta.selfCheck) && (
              <div style={{ fontSize: 12, marginBottom: 10, padding: '6px 10px', borderRadius: 8,
                background: (lastMeta.selfCheck || '').indexOf('未通过') >= 0 ? '#FBE9E2' : '#F7F4F1',
                color: (lastMeta.selfCheck || '').indexOf('未通过') >= 0 ? '#C14F28' : '#8A7A72' }}>
                {TASK_LABELS[lastMeta.taskType] ? '流程：' + TASK_LABELS[lastMeta.taskType] : ''}
                {lastMeta.selfCheck ? (TASK_LABELS[lastMeta.taskType] ? ' · ' : '') + lastMeta.selfCheck : ''}
              </div>
            )}
            {lastMeta && Array.isArray(lastMeta.top3) && lastMeta.top3.length > 0 && (
              <div style={{ fontSize: 12, marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#FDF6EF', color: '#5A4636', lineHeight: 1.7 }}>
                <div style={{ fontWeight: 600, color: '#D85A30', marginBottom: 4 }}>诊断结论（Top 3 问题 → 改造动作）</div>
                {lastMeta.top3.map((t, i) => (
                  <div key={i}>{i + 1}. <b>{t.problem}</b>{t.action ? ' → ' + t.action : ''}</div>
                ))}
              </div>
            )}
            {lastMeta && lastMeta.promptPack && (
              <div style={{ fontSize: 12, marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#FDF3EC', color: '#5A4636', lineHeight: 1.7, border: '1px dashed #E8B89A' }}>
                <div style={{ fontWeight: 600, color: '#C14F28', marginBottom: 4 }}>生图失败已转「提示词包」（可复制到任意生图工具）</div>
                {Object.entries(lastMeta.promptPack).map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 4 }}><b>{k}</b>：{String(v)}</div>
                ))}
              </div>
            )}
            {generating && !results.length ? (
              <div className="empty-hint"><Spin size="large" style={{ color: '#D85A30' }} /><div style={{ marginTop: 12 }}>AI 正在生成，请稍候…</div></div>
            ) : results.length ? (
              <div className="grid">
                {results.map((r, i) => (
                  <div className="cell" key={r.url + ':' + i}>
                    <div className="img"><img src={r.url} alt="" referrerPolicy="no-referrer" /></div>
                    <div className="ops">
                      <span onClick={() => download(r.url, 'main_' + (i + 1) + '.jpg')}><DownloadOutlined />下载</span>
                      <span onClick={() => doGenerate(1, true, true)}><ReloadOutlined />重绘</span>
                      <span className={canvasLoading ? 'disabled' : ''} onClick={() => canvasLoading ? null : openCanvasEditor(r.url)}><EditOutlined />{canvasLoading ? '准备中' : '画布'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-hint">上传产品图 → 识别卖点 → 点击「生成主图」开始出图</div>
            )}
          </div>
        </main>
      </div>

      {/* 历史记录弹窗 */}
      <Modal open={historyOpen} onCancel={() => setHistoryOpen(false)} footer={null} title="历史作品" width={720}>
        {historyImgs.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            {historyImgs.map((u, i) => (
              <div key={u + ':' + i} style={{ border: '1px solid #F0E2DB', borderRadius: 8, overflow: 'hidden', aspectRatio: '1/1' }}>
                <img src={u} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-hint">暂无历史作品</div>
        )}
      </Modal>

      {/* 九宫格拼接弹窗（3×3 本地合成，零 AI 费用） */}
      <Modal open={gridOpen} onCancel={() => setGridOpen(false)} footer={null} title="九宫格拼接（3 × 3）" width={600}>
        <input
          ref={gridFileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; handleGridFile(f); }}
        />
        <div style={{ fontSize: 12, color: '#8A7A72', marginBottom: 10, lineHeight: 1.7 }}>
          填满 9 张图 → 每张居中裁方 → 拼成一张九宫格。点空格上传本地图片，点已填格替换，<b>拖拽格子可换序</b>。
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => { const pre = results.slice(0, 9).map((r) => r.url); setGridImgs(Array(9).fill('').map((_, i) => pre[i] || '')); setGridResult(''); }}
            style={{ border: '1px solid #F0E2DB', background: '#FBF7F4', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#6B5D55' }}>
            📥 填入生成结果
          </button>
          <button type="button" onClick={() => { gridSlotRef.current = -1; fillGridFromHistory(); }}
            style={{ border: '1px solid #F0E2DB', background: '#FBF7F4', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#6B5D55' }}>
            🖼 从历史作品选
          </button>
          <button type="button" onClick={() => { setGridImgs(Array(9).fill('')); setGridResult(''); }}
            style={{ border: '1px solid #F0E2DB', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#B9A79E' }}>
            🧹 清空
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: gridImgs.filter(Boolean).length === 9 ? '#3F8600' : '#C14F28' }}>
            {gridImgs.filter(Boolean).length} / 9
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 }}>
          {gridImgs.map((u, i) => (
            <div
              key={i}
              draggable={!!u}
              onDragStart={() => { dragGridRef.current = i; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onGridDrop(i)}
              onClick={() => onGridSlotClick(i)}
              style={{
                position: 'relative', aspectRatio: '1/1', borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                border: '1px dashed ' + (u ? '#D8C4B6' : '#D8C4B6'), background: u ? '#fff' : '#FBF7F4',
              }}
            >
              {u ? (
                <>
                  <img src={u} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
                  <span
                    onClick={(e) => { e.stopPropagation(); setGridImgs((prev) => { const n = [...prev]; n[i] = ''; return n; }); }}
                    style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                  >✕</span>
                  <span style={{ position: 'absolute', left: 6, bottom: 4, fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,.6)' }}>{String(i + 1).padStart(2, '0')}</span>
                </>
              ) : (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#C9B8AC', fontSize: 20 }}>
                  ＋
                  <span style={{ fontSize: 11 }}>{String(i + 1).padStart(2, '0')}</span>
                </div>
              )}
            </div>
          ))}
        </div>
        {/* 选项 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 13, color: '#33251F' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={gridShowIndex} onChange={(e) => setGridShowIndex(e.target.checked)} /> 编号 01-09
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={gridGap} onChange={(e) => setGridGap(e.target.checked)} /> 白色缝隙
          </label>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            每格分辨率
            {[800, 1000, 1200].map((s) => (
              <button key={s} type="button" onClick={() => setGridCell(s)}
                style={{
                  padding: '2px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                  border: '1px solid ' + (gridCell === s ? '#D85A30' : '#E5DDD6'),
                  background: gridCell === s ? '#FBE9E2' : '#fff', color: gridCell === s ? '#C14F28' : '#6B5D55',
                }}>{s}</button>
            ))}
          </span>
        </div>
        {/* 生成 + 结果 */}
        <button
          type="button"
          disabled={gridBusy}
          onClick={doGridCompose}
          style={{
            width: '100%', padding: '10px 0', borderRadius: 8, cursor: gridBusy ? 'wait' : 'pointer',
            border: 'none', background: '#D85A30', color: '#fff', fontSize: 14, fontWeight: 600,
          }}
        >
          {gridBusy ? '拼接中…' : '▦ 生成九宫格'}
        </button>
        {gridResult && (
          <div style={{ marginTop: 12 }}>
            <img src={gridResult} alt="" referrerPolicy="no-referrer" style={{ width: '100%', borderRadius: 8, border: '1px solid #F0E2DB', display: 'block' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={() => download(gridResult, 'grid_9.jpg')}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer', border: '1px solid #E5DDD6', background: '#fff', color: '#33251F', fontSize: 13 }}>
                ⬇ 下载
              </button>
              <button type="button" onClick={() => { setResults((prev) => [...prev, { url: gridResult, thumbnail: gridResult }]); message.success('已插入生成结果区'); setGridOpen(false); }}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer', border: 'none', background: '#D85A30', color: '#fff', fontSize: 13, fontWeight: 600 }}>
                插入生成结果区
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* 历史选图弹窗（九宫格用） */}
      <Modal open={gridPickerOpen} onCancel={() => { setGridPickerOpen(false); gridSlotRef.current = -1; }} footer={null} title="从历史作品选择（点选填入）" width={720}>
        {historyImgs.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, maxHeight: '60vh', overflow: 'auto' }}>
            {historyImgs.map((u, i) => (
              <div key={u + ':' + i} onClick={() => pickFromHistory(u)}
                style={{ border: '1px solid #F0E2DB', borderRadius: 8, overflow: 'hidden', aspectRatio: '1/1', cursor: 'pointer' }}>
                <img src={u} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-hint">暂无历史作品</div>
        )}
      </Modal>

      {/* 改字画布编辑弹窗（稿定式三栏全屏画布） */}
      <Modal
        open={!!canvasInit}
        onCancel={() => setCanvasInit(null)}
        footer={null}
        closable={false}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', height: '100vh', paddingBottom: 0 }}
        bodyStyle={{ padding: 0, height: '100vh' }}
        destroyOnClose
        centered={false}
      >
        {canvasInit && <CanvasEditor init={canvasInit} onSaved={onCanvasSaved} onClose={() => setCanvasInit(null)} />}
      </Modal>

      {/* 存为背景模板弹窗 */}
      <Modal open={bgSaveOpen} onCancel={() => setBgSaveOpen(false)} onOk={saveBgTemplate} title="存为背景模板" okText="保存" cancelText="取消">
        <div style={{ marginBottom: 8, fontSize: 13, color: '#8A7A72' }}>模板名</div>
        <Input value={bgSaveName} onChange={(e) => setBgSaveName(e.target.value)} placeholder="例如：黑底细纹暖白纸卡" maxLength={30} />
      </Modal>

      {/* 收藏提示词弹窗 */}
      <Modal open={savePromptOpen} onCancel={() => setSavePromptOpen(false)} onOk={savePrompt} title="收藏提示词" okText="收藏" cancelText="取消">
        <div style={{ marginBottom: 8, fontSize: 13, color: '#8A7A72' }}>名称（留空自动取前 16 字）</div>
        <Input value={savePromptName} onChange={(e) => setSavePromptName(e.target.value)} placeholder="例如：香蕉梳白底主图" maxLength={20} />
        <div style={{ marginTop: 10, fontSize: 12, color: '#8A7A72', background: '#FAF6F3', padding: 8, borderRadius: 8, maxHeight: 80, overflow: 'auto', wordBreak: 'break-all' }}>{prompt}</div>
      </Modal>
    </div>
  );
}
